/**
 * Mirror of the CSS custom properties in src/index.css.
 *
 * Components consume tokens via the CSS-var indirection (`hsl(var(--primary))`)
 * so they line up 1:1 with the sovrn source. This theme object exists for
 * styled-components consumers who'd rather read theme values via the
 * `${({ theme }) => theme.color.primary}` idiom.
 */
export const theme = {
  /**
   * THE-1047's "short colour list" for the board header is already here, not
   * a new set of tokens: `liked` (one pink, hearts), `mutedForeground` (one
   * grey, for ✕ markers and secondary text), `foreground` (the near-black
   * ink). THE-1086 had briefly settled the board on `inkSoft` instead of
   * `foreground` as "the one ink" (near-identical but distinct shades).
   * THE-1185 reverses that: `foreground` (which computes to Kate's approved
   * `#38322E`) is the one near-black text color board-page-wide,
   * `mutedForeground` is the one grey — nothing else (not `inkSoft`, not
   * `mutedText`, not a literal hex/hsl grey) renders body text anywhere on
   * the board page. `inkSoft` stays in place as a token (45+ non-board call
   * sites still use it) — this is a per-page policy on which token board
   * components read, not a retirement of the token itself. The fourth piece
   * of the original list — each person's own colour as a pale wash behind
   * the header — isn't a flat token at all; it's generated per recipient by
   * `accentWash()` in `theaWeb/board/recipientAccent.ts` from the
   * emoji-keyed `ACCENTS` table. Brand/CTA accents (`clay`/`primary`) are a
   * separate category again — deliberately outside the 2-text-color rule,
   * same as a link's colour is not counted in Airbnb's "2 text colors"
   * either. See `src/theme/board-ui-standard.md` for the full rule.
   */
  color: {
    background: 'hsl(var(--background))',
    foreground: 'hsl(var(--foreground))',
    card: 'hsl(var(--card))',
    cardForeground: 'hsl(var(--card-foreground))',
    primary: 'hsl(var(--primary))',
    primaryForeground: 'hsl(var(--primary-foreground))',
    secondary: 'hsl(var(--secondary))',
    secondaryForeground: 'hsl(var(--secondary-foreground))',
    muted: 'hsl(var(--muted))',
    mutedForeground: 'hsl(var(--muted-foreground))',
    accent: 'hsl(var(--accent))',
    accentForeground: 'hsl(var(--accent-foreground))',
    liked: 'hsl(var(--liked))',
    likedForeground: 'hsl(var(--liked-foreground))',
    destructive: 'hsl(var(--destructive))',
    border: 'hsl(var(--border))',
    input: 'hsl(var(--input))',
    ring: 'hsl(var(--ring))',
    /* Brand constants pulled from sovrn inline styles. Pinned because they
       appear hard-coded in the source (HeroHeader.tsx, SiteHeader.tsx) rather
       than read from a CSS var. */
    // Rich clay (Kate, 2026-10-28, chosen against the real Save bar) replaces
    // the earlier #AF5B50. = hsl 7 43% 39%, white = 6.97:1, in step with
    // --primary. a11y row 113. Same value as `deepClay` below.
    clay: '#8E4339',
    inkSoft: '#3D3530',
    /* THE-1300 colour-roles groundwork: the hub's dark primary pills
       (HubFrameParts' HUB_PRIMARY_BG). Named so other near-black literals of
       this exact value can move here when their files are next touched —
       see board-ui-standard.md's "Colour roles" table. Not `inkSoft`
       (#3D3530) or `foreground` (#38322E): a third, distinct near-black. */
    ink: '#2A211C',
    cream: '#F5F0EB',
    /* Lighter cream used as the Results-page sticky-header background
       (ResultsPageAuth.tsx, CREAM constant). Distinct from `cream`. */
    creamLight: '#FAF7F2',
    /* Warm border used on the profile pill + tab strip divider
       (ResultsPageAuth.tsx:1370). */
    warmBorder: '#E8E5E0',
    /* PDP CTA-band roles (2026-07-16, board + guide ProductDetailSheet).
       neutralButton is the secondary-button fill — deliberately a step darker
       than cream/creamLight so it reads as a button against the sheet
       background; clayPressed is the darker clay for primary pressed states. */
    neutralButton: '#EEE8E0',
    neutralButtonPressed: '#E4DCD2',
    // Darker than the rich-clay `clay` fill (the old pressed shade was
    // lighter than clay). White on #75372F is 8.96:1.
    clayPressed: '#75372F',
    /* Sovrn-pinned auth-modal tokens. The auth surface uses a slightly cooler
       muted gray than the rest of the app and a flat error red — pinning here
       so the modal palette doesn't drift. */
    // Was #8A8A8A (3.05:1 on cream — failed WCAG AA for normal text). Darkened to
    // #6B6B6B (~4.7:1), matching the app's --muted-foreground (0 0% 42%). a11y row 119.
    mutedText: '#6B6B6B',
    errorRed: '#D94444',
    /* Destructive-action tokens (2026-09-01, account deletion — the product's
       first irreversible action). NOT `errorRed`: that is an error-TEXT colour
       on cream, and white text on #D94444 measures 4.32:1, which fails WCAG AA
       for normal text. #B3261E gives 6.5:1 against white (button fill) and
       5.8:1 on cream (error text), so one token serves both roles. Same
       darken-until-it-passes reasoning as the clay and mutedText notes above
       (a11y rows 113 / 119). `dangerPressed` is the hover/active step. */
    danger: '#B3261E',
    dangerPressed: '#8F1E18',
    /* THE-950 board spec, "Plum means gift guides": the one new solid-fill
       background this feature introduces, for the per-section "Dive deeper
       into [topic]" block. THE-1374 darkens it from #5B3A5E to #3B2436 — the
       Gift Guide Hub's "Finds under $25" band's own deeper plum (HubFrameParts
       .tsx's `HubBand`) — so the board's and the hub's plum bands read as the
       same colour; HubBand now reads this token too instead of its own
       literal, so the two cannot drift apart again. White text on #3B2436
       measures ~14:1 (comfortably past WCAG AA's 4.5:1 for normal text), same
       darken-until-it-passes reasoning as `danger`/`clay` above. Not the
       calendar's muted `PLUM` (#C2A3BE, a day-tile FILL meant to sit under
       dark text) — that shade is local to holidays.ts and isn't exported;
       this is a different, deeper value sized for white text on top of it. */
    plum: '#3B2436',
    /* Calendar + Coming Up day-type roles (THE-746 Phase 1, 2026-10-02 spec —
       colours locked by Kate). One token per event type, shared by the month
       grid's day fills, the event-card date pills and the Coming Up date tiles,
       so the two surfaces cannot drift apart. The spec's rule is explicit:
       "never ad-hoc near-shades" — if a day needs this colour, read it here.

       `deepClay` is the same value as `clay` (#8E4339, rich clay) since
       THE-1301 — both tokens are kept because calendar call sites read
       `deepClay` specifically. White on it is 6.97:1.
       Contrast for every pairing is pinned by calendarTokenContrast.test.ts, so
       darkening or lightening any of these fails the build rather than silently
       dropping below WCAG AA (the failure mode of a11y rows 113 / 119). */
    // Today — solid fill, white text. 11.50:1.
    darkChocolate: '#4E3326',
    // Your people (birthdays, anniversaries) — solid fill, white text. 6.97:1.
    deepClay: '#8E4339',
    // Your people, ALREADY PAST — the muted step of `deepClay`, paired with
    // `deepClay` itself as the text colour (5.27:1).
    //
    // Why a token instead of reusing the grid's mechanism (THE-793 item 4):
    // the month grid mutes a past day by dropping the whole CELL to
    // opacity 0.55, which composites deepClay to #C19892 — and a 14px date
    // label on #C19892 measures 2.71:1 in deepClay and 2.57:1 in white.
    // Neither clears AA, so the event-card pill cannot copy the cell's
    // literal colour. It stays in the same HUE FAMILY instead, which is what
    // the QA note actually asked for: a past friend pill read as neutral
    // grey (`warmBorder`) next to a rose grid cell. Its holiday twin needs no
    // new token — a past holiday pill is `linen` + `mochaText`, which is also
    // where `mocha` lands when the grid composites it at 0.55.
    deepClayPast: '#EADDDB',
    // Holidays — fill, paired with `mochaText` (never white: white on mocha is
    // 1.4:1). 7.48:1.
    mocha: '#EBE0D6',
    // The dark-brown text that rides on both `mocha` and `linen`.
    mochaText: '#5C3D2E',
    // The Coming Up strip's full-bleed band. Also carries `mochaText` (8.34:1).
    linen: '#F2EDE6',
    // THE-1296 — the calendar event card's "Done" state (menu-driven mark-done,
    // not a status the grid paints). White-background text only, 5.12:1 —
    // pinned by calendarTokenContrast.test.ts alongside the Phase 1 set above.
    doneGreen: '#3E7A4F',
    /* THE-1298 Fresh Find Fridays moment (board, Kate-approved 2026-10-28).
       Card/tile border — a pale rose a step lighter than `deepClay`, not
       `warmBorder` (that one is grey, not rose). */
    freshFindCardBorder: '#F1D9DC',
    // Same card's drop shadow — `deepClay`/`clay`'s rgb (142, 67, 57) at 8%.
    freshFindCardShadow: 'rgba(142, 67, 57, 0.08)',
    // The moment's own desktop scrim. Same ink rgb as `BoardRegenerateBanner`'s
    // scrim but a different alpha (0.45 vs 0.36), so it is its own token
    // rather than a shared one.
    freshFindScrim: 'rgba(38, 31, 26, 0.45)',
  },
  shadow: {
    soft: 'var(--shadow-soft)',
    card: 'var(--shadow-card)',
    lg: 'var(--shadow-lg)',
    xl: 'var(--shadow-xl)',
    /* THE-1303: the mobile board tab rail's pinned-only shadow — ink
       (#2A211C) at 12%. A plain literal rather than a CSS var (unlike the
       four above) so the computed box-shadow styled-components emits is
       this exact value, not an unresolved var() — jsdom's toHaveStyle
       doesn't resolve custom properties the way a real browser's computed
       style does. */
    railPinned: '0 2px 10px rgba(42, 33, 28, 0.12)',
  },
  gradient: {
    primary: 'var(--gradient-primary)',
    card: 'var(--gradient-card)',
    /* The HeroHeader CTA uses an inline gradient, not the --gradient-primary
       var. Surfacing it here so the Button primary variant can read it
       without re-deriving.
       THE-1301: both stops are rich clay now — solid, not a fade — same as
       --gradient-primary. Kept as a `linear-gradient(...)` string since
       ctaContrast.test.ts parses it. */
    cta: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary)))',
  },
  radius: {
    sm: 'calc(var(--radius) - 4px)',
    md: 'calc(var(--radius) - 2px)',
    lg: 'var(--radius)',
    xl: '12px',
    '2xl': '16px',
    '3xl': '24px',
    pill: '9999px',
  },
  /**
   * THE-1185 — the one pill spec for every text pill on the board (Update
   * picks, More like this, chip-tab scroll nudges): 40px tall, 16px side
   * padding, 8px gap, 14px/600 ink text, white fill, a 1px `warmBorder`
   * border (a soft shadow instead on a photo), a 16px icon. `text` is a flat
   * 14px — Kate's bullet gives the pill exactly one text size, with no
   * mobile/desktop step the way `theme.text`'s roles have (THE-1185: "14px/600
   * ink text", no second value named). Icon-only pills collapse to the
   * separate 36px circle (`iconCircle`) rather than shrinking this shape.
   * Mirrors `radius.pill` / `weight.semibold` as plain literals rather than
   * cross-references (this object is a flat `as const` literal, so a sibling
   * key can't read another sibling while the object is being built) — if
   * `weight.semibold` ever changes, update this too.
   */
  pill: {
    radius: '9999px',
    text: '14px',
    weight: 600,
    height: '40px',
    paddingX: '16px',
    gap: '8px',
    iconSize: '16px',
    border: '1px',
    /** The one 36px icon-only circle button (Kate's separate bullet) —
     *  not a shrunk text pill. */
    iconCircle: '36px',
  },
  font: {
    sans: "'Albert Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    serif: "'Source Serif 4', Georgia, 'Times New Roman', serif",
  },
  /**
   * Board page type scale (THE-1047, extended board-page-wide by THE-1185,
   * "Keeping it simple" / Airbnb lens): exactly four text-size roles — name,
   * section, body (tabs + tile), small — each a step smaller on phone than
   * desktop. One typeface for all of them — `font.sans`, never `font.serif`
   * — is the other half of that rule; this object only owns size. See
   * `src/theme/board-ui-standard.md` for the full rule (sizes + weights +
   * the two text colors + the pill spec together).
   *   - `name`: the recipient's name (BoardHeader's RecipientAnchor). 28/20.
   *   - `section`: a carousel/rail section heading at rest — "🔥 Trending"
   *     and its siblings (`BoardFeed`'s `SectionHeading`). 22/20.
   *   - `tabs`: a horizontal row of destination/section links at rest — the
   *     interest-tab strip (BoardChipTabs) AND the site nav (SiteNavLinks),
   *     which share this role because they are the same widget shape
   *     (THE-1185). Body text, 17/15.
   *   - `tile`: tile titles AND their counts — the progress card's title,
   *     the saves tile's count, the desktop pinned pill's count ("4 of 10",
   *     "♥ 41") — AND the product card's title (ResultsProductCard, THE-1185).
   *     Titles and counts share a role because the mock draws no line between
   *     them ("tile titles and counts" is one bullet). Also body text, 17/15
   *     — `tabs` and `tile` are the same desktop/mobile pair because THE-1185
   *     draws one "17 body" bucket, not two.
   *   - `small`: secondary/supporting text smaller than a tile title — the
   *     empty-saves placeholder, the band/sticker label, a pill's label
   *     (see `pill` below), and the product card's brand/price/link line.
   *     14/12 (THE-1185 bumped the desktop value from 13 to 14; see the
   *     ticket's explicit "the one change is small 13 → 14").
   * Values are the sizes already in use for each role today (BoardHeader's
   * V1 name row, BoardChipTabs, BoardProgressCard's title, the saves tile's
   * count) — this is the existing reality turned into one shared source, not
   * a redesign. New board-page work should read from here rather than
   * picking a new px value.
   */
  text: {
    name: { mobile: '20px', desktop: '28px' },
    /* `section`'s mobile value isn't in Kate's bullet list, which names only
       the desktop figure (22) — interpolated. 18px, not 20: at 20 this role
       was byte-identical to `name`'s own mobile size, collapsing the mobile
       scale to 3 sizes instead of 4 (QA 2026-10-25, PR #889). 18 sits
       strictly between `name` (20) and `tabs`/`tile` (15), preserving a
       distinct step for all four roles on phone too. Flag for design
       confirmation if a different mobile step is intended. */
    section: { mobile: '18px', desktop: '22px' },
    tabs: { mobile: '15px', desktop: '17px' },
    tile: { mobile: '15px', desktop: '17px' },
    small: { mobile: '12px', desktop: '14px' },
  },
  /** Two weights only — "nothing bold-bold" (THE-1047 spec, reinforced for
   *  the surfaces THE-1185 names: SiteNavLinks, BoardChipTabs,
   *  BoardHeaderCard, ResultsProductCard, the two pills). `semibold` is the
   *  one step up from body text on those surfaces; there is no third
   *  weight. This does NOT mean no `500`/`700` anywhere on the board page —
   *  `boardUiStandard.fitness.test.ts`'s `BASELINE` still carries real `500`/
   *  `700` literals in the many pre-existing board files THE-1185 left
   *  untouched (its own scope note / `board-ui-standard.md`'s), a QA round 4
   *  (2026-10-26, #889) correction to this comment's prior, broader claim. */
  weight: {
    regular: 400,
    semibold: 600,
  },
  breakpoint: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
} as const;

export type Theme = typeof theme;

declare module 'styled-components' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface DefaultTheme extends Theme {}
}
